import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/API';

const HOUSE_LABELS = { S: 'Senate', A: 'Assembly' };

const STATUS_LABELS = {
    new_member: 'New member',
    needs_office: 'Needs office',
    house_change: 'Changed houses',
    link_only: 'Link profile',
    needs_review: 'Check profile match',
};

// The session that opens in the coming December (or the one already open from December on).
const defaultSession = () => {
    const now = new Date();
    const year = now.getFullYear();
    const start = now.getMonth() === 11 ? year + 1 : year % 2 === 0 ? year + 1 : year;
    return `${start}-${start + 1}`;
};

const sessionChoices = () => {
    const first = parseInt(defaultSession().slice(0, 4), 10);
    return [first, first - 2].map(y => `${y}-${y + 1}`);
};

const formatLongDate = (iso) => {
    const [y, mo, d] = iso.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function AdminLegislativeTransition() {
    const [session, setSession] = useState(defaultSession());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [picks, setPicks] = useState({});
    const [closing, setClosing] = useState({});
    const [endStaff, setEndStaff] = useState({});

    const [working, setWorking] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [result, setResult] = useState(null);
    const [actionError, setActionError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const res = await API.getLegislativeTransition(session);
            const next = res.data;
            setData(next);
            const initialPicks = {};
            for (const a of next.arrivals || []) {
                initialPicks[a.legislators_tbl_id] = {
                    include: a.status !== 'needs_review',
                    profile_id: a.profile_id || '',
                    reopen: !!a.reuse_division_id,
                    ...a.suggested,
                };
            }
            setPicks(initialPicks);
            setClosing(Object.fromEntries((next.departing || []).map(d => [d.division_id, !d.possible_match])));
            setEndStaff({});
        } catch (err) {
            setData(null);
            setLoadError(err.response?.data?.msg || 'Failed to load the roster comparison.');
        } finally {
            setLoading(false);
        }
    }, [session]);

    useEffect(() => { setResult(null); setConfirming(false); setActionError(''); load(); }, [load]);

    const setPick = (id, key, value) => setPicks(p => ({ ...p, [id]: { ...p[id], [key]: value } }));

    const buildPayload = (dryRun) => {
        const create = [];
        const link = [];
        for (const a of data.arrivals) {
            const pick = picks[a.legislators_tbl_id];
            if (!pick?.include) continue;
            if (a.status === 'link_only') {
                link.push({ legislators_tbl_id: a.legislators_tbl_id, profile_id: a.profile_id });
                continue;
            }
            create.push({
                legislators_tbl_id: a.legislators_tbl_id,
                profile_id: pick.profile_id || null,
                profile: { first_name: a.first_name, last_name: a.last_name, political_party: pick.political_party },
                division: {
                    reuse_division_id: pick.reopen ? a.reuse_division_id : null,
                    division_name: pick.division_name,
                    street_address: pick.street_address,
                    suite_number: pick.suite_number,
                    city: pick.city,
                    state: pick.state,
                    zip_code: pick.zip_code,
                    website: pick.website,
                    division_phone: pick.division_phone,
                },
            });
        }
        const deactivate = data.departing
            .filter(d => closing[d.division_id])
            .map(d => ({ division_id: d.division_id, end_staff: !!endStaff[d.division_id] }));
        const closingIds = new Set(deactivate.map(d => d.division_id));
        for (const office of data.staff) {
            if (!office.active && endStaff[office.division_id] && !closingIds.has(office.division_id)) {
                deactivate.push({ division_id: office.division_id, end_staff: true });
            }
        }
        return { session, create, link, deactivate, dryRun };
    };

    const run = async (dryRun) => {
        setWorking(true);
        setActionError('');
        try {
            const res = await API.applyLegislativeTransition(buildPayload(dryRun));
            setResult(res.data);
            setConfirming(false);
            if (!dryRun) await load();
        } catch (err) {
            setActionError(err.response?.data?.msg || 'Failed to apply the changes.');
        } finally {
            setWorking(false);
        }
    };

    const selectedCount = data?.available
        ? data.arrivals.filter(a => picks[a.legislators_tbl_id]?.include).length + data.departing.filter(d => closing[d.division_id]).length
        : 0;
    const needsProfileChoice = data?.available && data.arrivals.some(a => a.status === 'needs_review' && picks[a.legislators_tbl_id]?.include && !picks[a.legislators_tbl_id]?.profile_id && picks[a.legislators_tbl_id]?.new_profile !== true);

    return (
        <div>
            <p className="text-muted small mb-3">
                Compares a session's roster with the member offices on file. Arrivals get a profile, an office and an Elected
                job starting on the swearing-in date. Offices whose member is no longer on the roster are closed, along with the
                member's Elected jobs and leadership posts. Staff jobs are only ended when you ask. Nothing changes until you apply.
            </p>

            <div className="ah-filters">
                <select className="admin-search-input ah-filter" value={session} onChange={e => setSession(e.target.value)}>
                    {sessionChoices().map(s => <option key={s} value={s}>{s.replace('-', '–')} session</option>)}
                </select>
                <button className="awards-btn" onClick={load} disabled={loading}>Re-check</button>
            </div>

            {loading && <div className="admin-loading">Loading…</div>}
            {loadError && <div className="ai-error mb-2">{loadError}</div>}

            {!loading && data && !data.available && (
                <div className="ah-form-note">
                    The {session.replace('-', '–')} roster hasn't been imported yet. It appears after the Legislature publishes it
                    and the nightly legislation scrape runs (members are sworn in {formatLongDate(data.dates.swearingIn)}). Check back then.
                </div>
            )}

            {!loading && data?.available && (
                <>
                    <div className="lt-tiles">
                        <div className="lt-tile"><strong>{data.counts.roster}</strong><span>on the roster</span></div>
                        <div className="lt-tile"><strong>{data.counts.unchanged}</strong><span>already set up</span></div>
                        <div className="lt-tile"><strong>{data.counts.arrivals}</strong><span>need an office</span></div>
                        <div className="lt-tile"><strong>{data.counts.departing}</strong><span>offices to close</span></div>
                        <div className="lt-tile"><strong>{data.counts.vacancies}</strong><span>vacant{data.vacancies.length > 0 ? ` (${data.vacancies.join(', ')})` : ''}</span></div>
                    </div>

                    <div className="ah-list-header">
                        <div className="ah-list-title">Arrivals ({data.arrivals.length})</div>
                    </div>
                    {data.arrivals.length === 0 ? (
                        <div className="text-muted small mb-3">Everyone on the roster already has an office.</div>
                    ) : data.arrivals.map(a => {
                        const pick = picks[a.legislators_tbl_id] || {};
                        const id = a.legislators_tbl_id;
                        return (
                            <div key={id} className={`lt-card ${pick.include ? '' : 'lt-card--off'}`}>
                                <label className="lt-card-head">
                                    <input type="checkbox" checked={!!pick.include} onChange={e => setPick(id, 'include', e.target.checked)} />
                                    <span className="lt-name">{a.name}</span>
                                    <span className="text-muted small">{HOUSE_LABELS[a.house]} · {a.district}{a.party ? ` · ${a.party}` : ''}</span>
                                    <span className="ah-badge ah-badge--edited">{STATUS_LABELS[a.status]}</span>
                                </label>

                                {a.status === 'house_change' && (
                                    <div className="lt-note">Currently in {a.from_office}. That office closes in the departures below.</div>
                                )}
                                {a.profile_id && <div className="lt-note">Existing profile: {a.profile_name}</div>}

                                {a.status === 'needs_review' && (
                                    <div className="lt-note">
                                        <div>These profiles look like the same person. Pick one, or create a new profile:</div>
                                        {a.candidates.map(c => (
                                            <label key={c.profile_id} className="lt-radio">
                                                <input type="radio" name={`p_${id}`} checked={pick.profile_id === c.profile_id} onChange={() => { setPick(id, 'profile_id', c.profile_id); setPick(id, 'include', true); }} />
                                                {c.name}{c.via_district && <span className="text-muted small"> (holds the {a.district} office under a different first name)</span>}
                                            </label>
                                        ))}
                                        <label className="lt-radio">
                                            <input type="radio" name={`p_${id}`} checked={!pick.profile_id && pick.new_profile === true} onChange={() => { setPick(id, 'profile_id', ''); setPick(id, 'new_profile', true); setPick(id, 'include', true); }} />
                                            Create a new profile
                                        </label>
                                    </div>
                                )}

                                {a.status === 'link_only' ? (
                                    <div className="lt-note">Already holds the seat; the roster row just needs linking to {a.profile_name}.</div>
                                ) : pick.include && (
                                    <>
                                        {a.reuse_division_id && (
                                            <label className="lt-radio">
                                                <input type="checkbox" checked={!!pick.reopen} onChange={e => setPick(id, 'reopen', e.target.checked)} />
                                                Reopen the existing office instead of creating a new one
                                            </label>
                                        )}
                                        <div className="ah-grid lt-grid">
                                            <div className="ai-field ah-span-2">
                                                <label className="ai-field-label">Office name</label>
                                                <input className="admin-search-input" value={pick.division_name || ''} onChange={e => setPick(id, 'division_name', e.target.value)} />
                                            </div>
                                            <div className="ai-field">
                                                <label className="ai-field-label">Room</label>
                                                <input className="admin-search-input" placeholder="Assigned in December" value={pick.suite_number || ''} onChange={e => setPick(id, 'suite_number', e.target.value)} />
                                            </div>
                                            <div className="ai-field">
                                                <label className="ai-field-label">Phone</label>
                                                <input className="admin-search-input" value={pick.division_phone || ''} onChange={e => setPick(id, 'division_phone', e.target.value)} />
                                            </div>
                                            <div className="ai-field ah-span-4">
                                                <label className="ai-field-label">Website (from the district pattern, verify)</label>
                                                <input className="admin-search-input" value={pick.website || ''} onChange={e => setPick(id, 'website', e.target.value)} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="ah-list-header mt-3">
                        <div className="ah-list-title">Offices to close ({data.departing.length})</div>
                    </div>
                    {data.departing.length === 0 ? (
                        <div className="text-muted small mb-3">No active office is missing from the roster.</div>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th className="admin-th-center">Close</th>
                                        <th>Office</th>
                                        <th>Holder</th>
                                        <th>Staff</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.departing.map(d => (
                                        <tr key={d.division_id}>
                                            <td className="admin-th-center" data-label="Close">
                                                <input type="checkbox" checked={!!closing[d.division_id]} onChange={e => setClosing(c => ({ ...c, [d.division_id]: e.target.checked }))} />
                                            </td>
                                            <td data-label="Office">
                                                <div>{d.name}</div>
                                                <div className="text-muted small">{HOUSE_LABELS[d.house]}{d.district ? ` · ${d.district}` : ''}</div>
                                                {d.possible_match && <div className="ah-badges"><span className="ah-badge ah-badge--status">Same last name as an unmatched roster member</span></div>}
                                                {d.moving_to && <div className="ah-badges"><span className="ah-badge ah-badge--edited">Moving to the {HOUSE_LABELS[d.moving_to.house]} ({d.moving_to.district})</span></div>}
                                            </td>
                                            <td data-label="Holder">
                                                {d.holders.length === 0 ? <span className="text-muted">No current holder</span> : d.holders.map(h => (
                                                    <div key={h.profile_id}>{h.name} <span className="text-muted small">{h.titles.join(', ')}</span></div>
                                                ))}
                                            </td>
                                            <td data-label="Staff">
                                                {d.staff_count === 0 ? <span className="text-muted">None</span> : (
                                                    <label className="lt-radio">
                                                        <input type="checkbox" checked={!!endStaff[d.division_id]} onChange={e => setEndStaff(s => ({ ...s, [d.division_id]: e.target.checked }))} />
                                                        End {d.staff_count} staff {d.staff_count === 1 ? 'job' : 'jobs'}
                                                    </label>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {data.staff.some(o => !o.active) && (
                        <>
                            <div className="ah-list-header mt-3">
                                <div className="ah-list-title">Staff still on closed offices</div>
                            </div>
                            <p className="text-muted small">These offices are already inactive but still list current staff. Tick one to end its staff jobs as of {formatLongDate(data.dates.priorEnd)}.</p>
                            {data.staff.filter(o => !o.active).map(o => (
                                <details key={o.division_id} className="lt-staff">
                                    <summary>
                                        <label className="lt-radio" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" checked={!!endStaff[o.division_id]} onChange={e => setEndStaff(s => ({ ...s, [o.division_id]: e.target.checked }))} />
                                        </label>
                                        {o.name} <span className="text-muted small">· {o.staff.length} current</span>
                                    </summary>
                                    <ul>{o.staff.map(s => <li key={s.job_id}>{s.name} <span className="text-muted small">{s.title}</span></li>)}</ul>
                                </details>
                            ))}
                        </>
                    )}

                    {data.leadership.some(l => !l.on_roster) && (
                        <>
                            <div className="ah-list-header mt-3">
                                <div className="ah-list-title">Leadership posts held by people off the roster</div>
                            </div>
                            <ul className="lt-leadership">
                                {data.leadership.filter(l => !l.on_roster).map(l => (
                                    <li key={l.id}>{l.name} · {l.title} ({HOUSE_LABELS[l.house]}) <span className="text-muted small">ends when their office is closed</span></li>
                                ))}
                            </ul>
                        </>
                    )}
                    <p className="text-muted small mt-2">
                        Leadership posts for continuing members (Speaker, Pro Tem, floor leaders, whips) change at the caucus votes
                        after the swearing-in; update those in the profile's leadership section when they are announced.
                    </p>

                    {actionError && <div className="ai-error mb-2">{actionError}</div>}
                    {result && (
                        <div className="ah-form-note">
                            {result.dryRun ? 'Preview only, nothing was saved. ' : 'Applied. '}
                            {result.created} offices created, {result.reopened} reopened, {result.linked} roster rows linked,
                            {' '}{result.deactivated} offices closed ({result.electedEnded} elected jobs, {result.leadershipEnded} leadership posts, {result.staffEnded} staff jobs ended).
                        </div>
                    )}
                    {needsProfileChoice && <div className="ai-error mb-2">Choose a profile match (or create a new profile) for the highlighted arrivals.</div>}

                    <div className="ah-form-actions mt-2">
                        {confirming ? (
                            <>
                                <span className="small">Apply {selectedCount} change{selectedCount === 1 ? '' : 's'} as of {formatLongDate(data.dates.swearingIn)}?</span>
                                <button className="awards-save-btn" onClick={() => run(false)} disabled={working}>
                                    {working ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Yes, apply'}
                                </button>
                                <button className="awards-btn" onClick={() => setConfirming(false)} disabled={working}>Cancel</button>
                            </>
                        ) : (
                            <>
                                <button className="awards-btn" onClick={() => run(true)} disabled={working || selectedCount === 0 || needsProfileChoice}>Preview</button>
                                <button className="awards-save-btn" onClick={() => setConfirming(true)} disabled={working || selectedCount === 0 || needsProfileChoice}>Apply changes</button>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
